import { store } from '@spartalabs/pdc-core';
import axios from 'axios';
import {
  FineStatusStage,
  MultasFine,
  MultasKpiSummary,
  MultasPlateGroup,
  MultasStatusTimelineSummary,
  TopVehicleRankingItem,
  TrafficLightBucket,
} from '../models/multas.types';

export const EMPTY_FINES_KPIS: MultasKpiSummary = {
  totalFines: 0,
  vehiclesInvolved: 0,
  awaitingDriverIdentification: 0,
  totalAuthorityAmount: 0,
  platesWithFines: 0,
  fleetTotal: 0,
};

export const EMPTY_FINES_STATUS: MultasStatusTimelineSummary = {
  notification: 0,
  driverIdentification: 0,
  paidToAuthority: 0,
  reimbursed: 0,
};

export const EMPTY_FINES_PLATE_GROUPS: MultasPlateGroup[] = [];

type InfractionApi = Omit<
  MultasFine,
  'statusStage' | 'driverIdentificationState' | 'trafficLightBucket'
> & {
  statusStage?: string | null;
  driverIdentificationState?: string | null;
  trafficLightBucket?: string | null;
};

export interface FinesGeoAgrupadoraResult {
  plateGroups: MultasPlateGroup[];
  finesByPlate: Record<string, MultasFine[]>;
}

type FinesGeoAgrupadoraApiResponse = {
  plateGroups?: MultasPlateGroup[] | null;
  finesByPlate?: Record<string, InfractionApi[] | null> | null;
};

type FinesGeoPlatesApiResponse =
  | MultasPlateGroup[]
  | {
      items?: MultasPlateGroup[];
      Items?: MultasPlateGroup[];
    };

function normalizePlatesResponse(
  data: FinesGeoPlatesApiResponse | null | undefined,
): MultasPlateGroup[] {
  if (!data) return EMPTY_FINES_PLATE_GROUPS;
  if (Array.isArray(data)) return data;
  return data.items ?? data.Items ?? EMPTY_FINES_PLATE_GROUPS;
}

function authHeaders() {
  const token = store.getState().authSlicer.user.token;
  const baseUrl = process.env.REACT_APP_URL;
  if (!token || !baseUrl) return null;
  return {
    baseUrl,
    headers: { Authorization: `Bearer ${token}` },
  };
}

function parseGroupId(groupId: string | null | undefined) {
  const parsed = Number(groupId);
  if (!parsed || Number.isNaN(parsed)) return null;
  return parsed;
}

function asStage(value?: string | null): FineStatusStage {
  if (
    value === FineStatusStage.DriverIdentification ||
    value === FineStatusStage.PaidToAuthority ||
    value === FineStatusStage.Reimbursed
  ) {
    return value;
  }
  return FineStatusStage.Notification;
}

function asTrafficLightBucket(value?: string | null): TrafficLightBucket | null {
  if (value === TrafficLightBucket.Urgent) return TrafficLightBucket.Urgent;
  if (value === TrafficLightBucket.Approaching) return TrafficLightBucket.Approaching;
  if (value === TrafficLightBucket.OnTime) return TrafficLightBucket.OnTime;
  return null;
}

function mapInfraction(item: InfractionApi): MultasFine {
  return {
    ...item,
    lakeStatus: item.lakeStatus ?? '',
    pagaAoOrgao: Boolean(item.pagaAoOrgao),
    reembolsavelAVamos: Boolean(item.reembolsavelAVamos),
    statusStage: asStage(item.statusStage),
    driverIdentificationState:
      item.driverIdentificationState === 'identified' ||
      item.driverIdentificationState === 'notIdentified' ||
      item.driverIdentificationState === 'pending'
        ? item.driverIdentificationState
        : null,
    trafficLightBucket: asTrafficLightBucket(item.trafficLightBucket),
  };
}

function mapInfractionList(items: InfractionApi[] | null | undefined): MultasFine[] {
  return (items ?? []).map(mapInfraction);
}

export async function fetchFinesGeoKpis(
  groupId: string | null | undefined,
): Promise<MultasKpiSummary> {
  const parsedGroupId = parseGroupId(groupId);
  const auth = authHeaders();
  if (!parsedGroupId || !auth) return EMPTY_FINES_KPIS;

  const response = await axios.get<MultasKpiSummary>(
    `${auth.baseUrl}/api/FinesGeo/${parsedGroupId}/kpis`,
    { headers: auth.headers },
  );
  return response.data ?? EMPTY_FINES_KPIS;
}

export async function fetchFinesGeoStatus(
  groupId: string | null | undefined,
): Promise<MultasStatusTimelineSummary> {
  const parsedGroupId = parseGroupId(groupId);
  const auth = authHeaders();
  if (!parsedGroupId || !auth) return EMPTY_FINES_STATUS;

  const response = await axios.get<MultasStatusTimelineSummary>(
    `${auth.baseUrl}/api/FinesGeo/${parsedGroupId}/status`,
    { headers: auth.headers },
  );
  return response.data ?? EMPTY_FINES_STATUS;
}

export async function fetchFinesGeoTopVehicles(
  groupId: string | null | undefined,
): Promise<TopVehicleRankingItem[]> {
  const parsedGroupId = parseGroupId(groupId);
  const auth = authHeaders();
  if (!parsedGroupId || !auth) return [];

  const response = await axios.get<TopVehicleRankingItem[]>(
    `${auth.baseUrl}/api/FinesGeo/${parsedGroupId}/top-vehicles`,
    { headers: auth.headers },
  );
  return response.data ?? [];
}

export async function fetchAllFinesGeoPlatesByGroup(
  groupId: string | null | undefined,
): Promise<MultasPlateGroup[]> {
  const parsedGroupId = parseGroupId(groupId);
  const auth = authHeaders();
  if (!parsedGroupId || !auth) return EMPTY_FINES_PLATE_GROUPS;

  const response = await axios.get<FinesGeoPlatesApiResponse>(
    `${auth.baseUrl}/api/FinesGeo/${parsedGroupId}/plates`,
    { headers: auth.headers },
  );
  return normalizePlatesResponse(response.data);
}

export async function fetchFinesGeoAgrupadora(
  groupId: string | null | undefined,
): Promise<FinesGeoAgrupadoraResult> {
  const parsedGroupId = parseGroupId(groupId);
  const auth = authHeaders();
  if (!parsedGroupId || !auth) {
    return { plateGroups: EMPTY_FINES_PLATE_GROUPS, finesByPlate: {} };
  }

  const response = await axios.get<FinesGeoAgrupadoraApiResponse>(
    `${auth.baseUrl}/api/FinesGeo/${parsedGroupId}/agrupadora`,
    { headers: auth.headers },
  );

  const plateGroups = normalizePlatesResponse(response.data?.plateGroups ?? []);
  const finesByPlate: Record<string, MultasFine[]> = {};
  const rawByPlate = response.data?.finesByPlate ?? {};

  Object.entries(rawByPlate).forEach(([plate, items]) => {
    finesByPlate[plate] = mapInfractionList(items);
  });

  plateGroups.forEach(group => {
    if (finesByPlate[group.plate] === undefined) {
      finesByPlate[group.plate] = [];
    }
  });

  return { plateGroups, finesByPlate };
}

export async function fetchFinesGeoInfractions(
  groupId: string | null | undefined,
  plate: string,
): Promise<MultasFine[]> {
  const parsedGroupId = parseGroupId(groupId);
  const auth = authHeaders();
  if (!parsedGroupId || !auth || !plate) return [];

  const response = await axios.get<InfractionApi[]>(
    `${auth.baseUrl}/api/FinesGeo/${parsedGroupId}/plates/${encodeURIComponent(plate)}/infractions`,
    { headers: auth.headers },
  );
  return mapInfractionList(response.data);
}

export async function fetchFinesGeoNotifications(
  groupId: string | null | undefined,
): Promise<MultasFine[]> {
  const parsedGroupId = parseGroupId(groupId);
  const auth = authHeaders();
  if (!parsedGroupId || !auth) return [];

  const response = await axios.get<InfractionApi[]>(
    `${auth.baseUrl}/api/FinesGeo/${parsedGroupId}/notifications`,
    { headers: auth.headers },
  );
  return mapInfractionList(response.data);
}
